import dayjs from 'dayjs';

import type {
  SaveClassModuleDisciplineViewModel,
  SaveClassModuleRecurrenceViewModel,
  SaveClassModuleViewModel,
} from '@/shared/api/generated';

import type { ClassModuleFormValues } from '../types/classModuleForm';

/**
 * Converte a data ISO (`YYYY-MM-DD`) do formulário em data-hora ISO para o
 * backend. Envia a data de calendário como meia-noite UTC, sem conversão de
 * fuso, para preservar o dia escolhido no DatePicker.
 */
function toIsoDateTime(value: string): string | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T00:00:00.000Z`;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toISOString() : null;
}

/**
 * Monta o `SaveClassModuleViewModel` (payload de POST/PUT) a partir dos valores
 * do formulário: datas em ISO, turmas reduzidas a `classStudentIds`, e
 * disciplinas/recorrências no formato de escrita (`weekDay`/`monthDay` conforme
 * o tipo da recorrência).
 *
 * @param form - Valores atuais do formulário de módulo.
 * @returns Payload pronto para envio à API.
 */
export function buildSaveViewModel(form: ClassModuleFormValues): SaveClassModuleViewModel {
  const disciplines: SaveClassModuleDisciplineViewModel[] = form.disciplines.map(d => {
    const recurrences: SaveClassModuleRecurrenceViewModel[] = d.recurrences.map(r => ({
      type: r.type,
      interval: r.interval,
      weekDay: r.type === 1 ? r.weekDay : null,
      monthDay: r.type === 2 ? r.monthDay : null,
      startTime: r.startTime || null,
      endTime: r.endTime || null,
    }));

    return {
      disciplineId: d.disciplineId,
      classroomId: d.classroomId,
      teacherPersonId: d.teacherPersonId,
      quantity: d.quantity,
      type: d.type,
      planningWithExam: d.planningWithExam,
      useMoveQA: d.useMoveQA,
      isVisualizationAttendance: d.isVisualizationAttendance,
      recurrences,
    };
  });

  return {
    description: form.description,
    dateStart: toIsoDateTime(form.dateStart),
    dateFinish: toIsoDateTime(form.dateFinish),
    companyId: form.companyId,
    ...(form.modality !== null ? { modality: form.modality } : {}),
    classStudentIds: form.linkedClasses.map(c => c.classStudentId),
    disciplines,
  };
}
